import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as ExecuteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { workflowService } from '../services/workflowService';
import { useNotificationStore } from '../store/notificationStore';
import type { WorkflowDefinitionDto, WorkflowFilters } from '../services/workflowService';
import { TextField } from '../@core/components/mui';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { PageListLayout } from '../components/layouts/PageListLayout';

/**
 * Workflow list page component.
 */
export const WorkflowListPage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [workflows, setWorkflows] = useState<WorkflowDefinitionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState<WorkflowFilters>({
    pageNumber: 1,
    pageSize: 10,
    searchTerm: '',
    isActive: true,
  });

  const loadWorkflows = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await workflowService.getWorkflows(filters);
      setWorkflows(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load workflows';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWorkflows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.pageNumber, filters.pageSize, filters.searchTerm, filters.isActive]);

  const handleDelete = async (workflow: WorkflowDefinitionDto) => {
    if (!window.confirm(`Are you sure you want to delete "${workflow.name}"?`)) return;
    try {
      await workflowService.deleteWorkflow(workflow.id);
      addNotification('Workflow deleted successfully', 'success');
      await loadWorkflows();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to delete workflow', 'error');
    }
  };

  const handleExecute = async (workflow: WorkflowDefinitionDto) => {
    try {
      const response = await workflowService.executeWorkflow(workflow.id);
      addNotification(`Workflow execution started: ${response.executionId}`, 'success');
      navigate(`/workflows/${workflow.id}`);
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to execute workflow', 'error');
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'success' : 'default';
  };

  const getWorkflowTypeLabel = (type: string) => {
    switch (type) {
      case 'Sequential':
        return 'Sequential';
      case 'Parallel':
        return 'Parallel';
      case 'Conditional':
        return 'Conditional';
      case 'Custom':
        return 'Custom';
      default:
        return type;
    }
  };

  // Define table columns
  const tableColumns: DataTableColumn<WorkflowDefinitionDto>[] = [
    {
      id: 'name',
      label: 'Name',
      minWidth: 200,
      render: (workflow) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {workflow.name}
          </Typography>
          {workflow.description && (
            <Typography variant="caption" color="text.secondary">
              {workflow.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'workflowType',
      label: 'Type',
      minWidth: 120,
      render: (workflow) => getWorkflowTypeLabel(workflow.workflowType),
    },
    {
      id: 'patternType',
      label: 'Pattern',
      minWidth: 120,
      render: (workflow) => (workflow.patternType !== 'None' ? workflow.patternType : '-'),
    },
    {
      id: 'version',
      label: 'Version',
      minWidth: 80,
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      render: (workflow) => (
        <Chip
          label={workflow.isActive ? 'Active' : 'Inactive'}
          size="small"
          color={getStatusColor(workflow.isActive) as any}
        />
      ),
    },
    {
      id: 'features',
      label: 'Features',
      minWidth: 150,
      render: (workflow) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {workflow.supportsCheckpointing && (
            <Chip label="Checkpoint" size="small" variant="outlined" />
          )}
          {workflow.supportsStreaming && (
            <Chip label="Streaming" size="small" variant="outlined" />
          )}
        </Box>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 180,
      align: 'right',
      render: (workflow) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="View">
            <IconButton size="small" onClick={() => navigate(`/workflows/${workflow.id}`)}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => navigate(`/workflows/${workflow.id}/edit`)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Execute">
            <IconButton size="small" onClick={() => handleExecute(workflow)}>
              <ExecuteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDelete(workflow)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <PageListLayout
      title="Workflows"
      createButton={{ label: 'Create Workflow', onClick: () => navigate('/workflows/new'), icon: <AddIcon /> }}
      filters={
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <TextField
            label="Search workflows"
            size="small"
            value={filters.searchTerm || ''}
            onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value, pageNumber: 1 })}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.isActive === undefined ? '' : filters.isActive ? 'active' : 'inactive'}
              label="Status"
              onChange={(e) => {
                const value = e.target.value as string;
                setFilters({
                  ...filters,
                  isActive: value === '' ? undefined : value === 'active',
                  pageNumber: 1,
                });
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>
      }
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      isEmpty={workflows.length === 0}
      emptyState={{
        title: 'No workflows found',
        description: 'Try adjusting your filters or create a new workflow to get started.',
        action: { label: 'Create Workflow', onClick: () => navigate('/workflows/new') },
      }}
      pagination={{
        page: filters.pageNumber || 1,
        totalPages: Math.ceil(totalCount / (filters.pageSize || 10)),
        onChange: (page) => setFilters({ ...filters, pageNumber: page }),
      }}
    >
      <DataTable
        columns={tableColumns}
        rows={workflows}
        loading={false}
      />
    </PageListLayout>
  );
};
